<script setup>
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Calendar from "primevue/calendar";
import Checkbox from "primevue/checkbox";
import Message from "primevue/message";
import Dropdown from "primevue/dropdown";
import { computed, ref } from "vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { useToast } from "primevue/usetoast";
import { usePage } from "@inertiajs/inertia-vue3";

const props = defineProps({
    company: {
        type: Object,
        required: true,
    },
    showBackButton: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['saved', 'cancel']);

const page = usePage();
const toast = useToast();
const errors = ref({});

const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');

const timezones = [
    { label: 'Europe/Zurich', value: 'Europe/Zurich' },
    { label: 'Europe/Berlin', value: 'Europe/Berlin' },
    { label: 'Europe/Vienna', value: 'Europe/Vienna' },
    { label: 'Europe/London', value: 'Europe/London' },
];

const form = useForm({
    name:         props.company.name,
    email:        props.company.email,
    phone:        props.company.phone,
    mobile:       props.company.mobile,
    street_name:  props.company.street_name,
    postcode:     props.company.postcode,
    city:         props.company.city,
    country:      props.company.country,
    domain:       props.company.domain,
    foundation:   props.company.foundation ? new Date(props.company.foundation) : null,
    description:  props.company.description,
    timezone:     props.company.timezone || 'Europe/Zurich',
    is_active:    props.company.is_active
});

const submit = () => {
    errors.value = {};
    form.put(`/management/companies/${props.company.uuid}`, {
        preserveState: true,
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Erfolgreich',
                detail: 'Unternehmen wurde erfolgreich aktualisiert.',
                life: 5000,
            });
            emit('saved');
        },
        onError: (err) => {
            errors.value = err;
            toast.add({
                severity: 'error',
                summary: 'Fehler beim Speichern',
                detail: 'Bitte prüfen Sie die Eingaben.',
                life: 5000,
            });
        },
    });
};

const hasErrors = computed(() => Object.values(errors.value).some(v => v));
</script>

<template>
    <div class="text-sm">

        <!-- Error banner -->
        <Message v-if="hasErrors" severity="error" class="mb-6">
            <div v-for="(error, key) in errors" :key="key">{{ error }}</div>
        </Message>

        <form @submit.prevent="submit" class="space-y-8" novalidate>
            <div class="space-y-8">

                <!-- Section 1: Basic Info -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">1</div>
                        <h2 class="text-lg font-bold text-gray-700">Grundinformationen</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="cf-name" class="font-bold text-gray-600">Name des Unternehmens *</label>
                            <InputText id="cf-name" v-model="form.name" :class="{'p-invalid': errors.name}" class="w-full" @input="errors.name = null" />
                            <small v-if="errors.name" class="p-error">{{ errors.name }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="cf-email" class="font-bold text-gray-600">E-Mail Adresse *</label>
                            <InputText id="cf-email" type="email" v-model="form.email" :class="{'p-invalid': errors.email}" class="w-full" @input="errors.email = null" />
                            <small v-if="errors.email" class="p-error">{{ errors.email }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="cf-domain" class="font-bold text-gray-600">Webseite / Domain</label>
                            <InputText id="cf-domain" v-model="form.domain" class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="cf-foundation" class="font-bold text-gray-600">Gründungsdatum *</label>
                            <Calendar id="cf-foundation" v-model="form.foundation"
                                      dateFormat="dd.mm.yy"
                                      :class="{'p-invalid': errors.foundation}"
                                      class="w-full" inputClass="w-full" showIcon @date-select="errors.foundation = null" />
                            <small v-if="errors.foundation" class="p-error">{{ errors.foundation }}</small>
                        </div>
                    </div>
                </div>

                <!-- Section 2: Address & Contact -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">2</div>
                        <h2 class="text-lg font-bold text-gray-700">Standort & Kontakt</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="cf-street" class="font-bold text-gray-600">Strasse & Nr. *</label>
                            <InputText id="cf-street" v-model="form.street_name" :class="{'p-invalid': errors.street_name}" class="w-full" @input="errors.street_name = null" />
                            <small v-if="errors.street_name" class="p-error">{{ errors.street_name }}</small>
                        </div>

                        <div class="grid grid-cols-3 gap-4">
                            <div class="flex flex-col gap-2 col-span-1">
                                <label for="cf-postcode" class="font-bold text-gray-600">PLZ *</label>
                                <InputText id="cf-postcode" v-model="form.postcode" :class="{'p-invalid': errors.postcode}" class="w-full" @input="errors.postcode = null" />
                                <small v-if="errors.postcode" class="p-error">{{ errors.postcode }}</small>
                            </div>
                            <div class="flex flex-col gap-2 col-span-2">
                                <label for="cf-city" class="font-bold text-gray-600">Stadt *</label>
                                <InputText id="cf-city" v-model="form.city" :class="{'p-invalid': errors.city}" class="w-full" @input="errors.city = null" />
                                <small v-if="errors.city" class="p-error">{{ errors.city }}</small>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="cf-phone" class="font-bold text-gray-600">Telefon *</label>
                            <InputText id="cf-phone" v-model="form.phone" :class="{'p-invalid': errors.phone}" class="w-full" maxlength="10" @input="errors.phone = null" />
                            <small v-if="errors.phone" class="p-error">{{ errors.phone }}</small>
                        </div>

                        <div class="flex flex-col gap-2">
                            <label for="cf-mobile" class="font-bold text-gray-600">Mobiltelefon</label>
                            <InputText id="cf-mobile" v-model="form.mobile" class="w-full" maxlength="10" />
                        </div>
                    </div>
                </div>

                <!-- Section 3: Advanced Settings -->
                <div class="bg-gray-50/50 p-8 rounded-2xl border border-gray-100">
                    <div class="flex items-center gap-3 mb-6">
                        <div class="w-10 h-10 rounded-full bg-black text-[#FFF053] flex items-center justify-center font-bold">3</div>
                        <h2 class="text-lg font-bold text-gray-700">Erweiterte Einstellungen</h2>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        <div class="flex flex-col gap-2">
                            <label for="cf-timezone" class="font-bold text-gray-600">Zeitzone</label>
                            <Dropdown id="cf-timezone" v-model="form.timezone"
                                      :options="timezones" optionLabel="label" optionValue="value"
                                      class="w-full" />
                        </div>

                        <div class="flex flex-col gap-2 justify-end">
                            <div class="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                                <Checkbox id="cf-is_active" v-model="form.is_active" :binary="true" />
                                <label for="cf-is_active" class="font-bold text-gray-700 cursor-pointer">Unternehmensprofil aktiv</label>
                            </div>
                        </div>

                        <div class="flex flex-col gap-2 md:col-span-2">
                            <label for="cf-description" class="font-bold text-gray-600">Interne Beschreibung</label>
                            <Textarea id="cf-description" v-model="form.description" rows="4" class="w-full" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="flex justify-between items-center pt-8 border-t mt-10">
                <Button v-if="showBackButton"
                        type="button"
                        label="Abbrechen"
                        severity="secondary"
                        outlined
                        class="px-8 py-3 rounded-xl border-gray-300 font-bold"
                        @click="emit('cancel')" />
                <div v-else></div>

                <Button type="submit"
                        class="!bg-black !text-[#FFF053] !border-none px-10 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-gray-200"
                        :loading="form.processing"
                        :disabled="form.processing">
                    <i class="pi pi-check" />
                    Änderungen speichern
                </Button>
            </div>
        </form>
    </div>
</template>
